import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { SourceDetailPanel } from '../components/connections/SourceDetailPanel';
import { useSourceReadModels } from '../hooks/useSourceReadModels';
import { recordSlice05ConnectionMarker } from '../lib/source-read-model/instrumentation';

export function SourceDetail() {
  const { id } = useParams<{ id: string }>();
  const { isLoading, models, error } = useSourceReadModels();
  const source = models.find(model => model.id === id);

  useEffect(() => {
    if (isLoading || error) return;
    recordSlice05ConnectionMarker({
      kind: 'source_detail_render',
      status: source ? 'success' : 'blocked',
      sourceId: source?.id,
      reasonCode: source ? `display_state:${source.displayState}` : 'unsupported_source',
    });
  }, [error, isLoading, source]);

  if (isLoading) {
    return (
      <div data-testid="source-detail-loading" className="mx-auto max-w-3xl rounded-lg border border-neutral-200 bg-white/70 p-5 text-sm text-neutral-500">
        Loading source detail...
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="source-detail-error" className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!source) {
    return (
      <div data-testid="source-detail-unsupported" data-source-id={id ?? ''} className="mx-auto max-w-3xl rounded-lg border border-neutral-200 bg-white/80 p-5 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Unsupported source</p>
        <h1 className="mt-1 font-serif text-2xl font-semibold text-neutral-950">Source unavailable</h1>
        <p className="mt-2 text-sm text-neutral-600">
          This source is not part of the local Connections read model.
        </p>
        <Link className="mt-4 inline-flex rounded-md border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50" to="/connections">
          Back to Connections
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="source-detail-route" className="mx-auto grid w-full max-w-3xl gap-4 pb-10">
      <Link className="text-sm font-medium text-neutral-600 hover:text-neutral-900" to="/connections">
        Back to Connections
      </Link>
      <SourceDetailPanel source={source} />
    </div>
  );
}

export default SourceDetail;
